import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Library, Plus, Trash2, CheckCircle2, Users, UserRound } from "lucide-react";
import { GRADE_GROUPS } from "@/lib/subjects";

export default function ReadingBooks() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isPicker = user?.role === "admin" || user?.role === "koc";
  const isAdmin = user?.role === "admin";
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [students, setStudents] = useState([]);
  const [books, setBooks] = useState([]);
  const [name, setName] = useState("");
  const [examDate, setExamDate] = useState("");
  const [targetType, setTargetType] = useState(params.get("grade") ? "class" : "student");
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 1);
  const [clsSection, setClsSection] = useState(params.get("section") || "");

  const classStudents = students.filter((s) => s.grade === clsGrade && (!clsSection || s.section === clsSection));

  useEffect(() => { if (isPicker) api.get("/students").then(({ data }) => setStudents(data)); }, [isPicker]);
  useEffect(() => {
    if (studentId || isPicker) return;
    api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); });
  }, [studentId, isPicker]);

  const load = useCallback(async () => {
    if (!studentId) return;
    const { data } = await api.get("/reading-books", { params: { student_id: studentId } });
    setBooks(data?.books || []);
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const save = async (list) => {
    try {
      const { data } = await api.post("/reading-books", { student_id: studentId, books: list });
      setBooks(data.books || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const add = async () => {
    if (!name.trim()) return toast.error("Kitap adı gerekli");
    if (targetType === "class") {
      try {
        const { data } = await api.post("/reading-books/assign-class", { grade: clsGrade, section: clsSection || null, name: name.trim(), exam_date: examDate });
        setName(""); setExamDate("");
        toast.success(`${data.count} öğrenciye kitap eklendi ✓`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (books.length >= 30) return toast.error("En fazla 30 kitap");
    await save([...books, { name: name.trim(), exam_date: examDate, approved: false }]);
    setName(""); setExamDate("");
    toast.success("Kitap eklendi ✓");
  };

  const approve = async (i, approved) => {
    try {
      const { data } = await api.patch("/reading-books/approve", { student_id: studentId, index: i, approved });
      setBooks(data.books || []);
      toast.success(approved ? "Sınav onaylandı ✓" : "Onay kaldırıldı");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = (i) => save(books.filter((_, j) => j !== i));

  const approvedCount = books.filter((b) => b.approved).length;
  const pct = books.length ? Math.round((approvedCount / books.length) * 100) : 0;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><Library className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Okuma Kitabı Takibi</h1>
            <p className="text-slate-500">Öğrencinin okuyacağı 30 kitap ve sınav tarihleri.{studentName ? ` · ${studentName}` : ""}</p>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-sm">
          <div className="text-2xl font-extrabold text-emerald-600">{approvedCount}/{books.length || 30}</div>
          <div className="text-xs font-semibold text-slate-500">onaylı sınav</div>
        </div>
      </div>

      {isPicker && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <label className="block text-sm font-semibold text-slate-700">Hedef Seç</label>
            <div className="flex rounded-xl bg-slate-100 p-1">
              <button data-testid="book-target-student" onClick={() => setTargetType("student")} className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-bold transition ${targetType === "student" ? "bg-emerald-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
                <UserRound className="h-4 w-4" /> Tek Öğrenci
              </button>
              <button data-testid="book-target-class" onClick={() => setTargetType("class")} className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-bold transition ${targetType === "class" ? "bg-indigo-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
                <Users className="h-4 w-4" /> Tüm Sınıf
              </button>
            </div>
          </div>
          {targetType === "student" ? (
            <ClassStudentSelect students={students} value={studentId} onChange={(v) => { setStudentId(v); setStudentName(students.find((s) => s.id === v)?.name || ""); }} testid="book-student-select" placeholder="Seçiniz…" />
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <select data-testid="book-class-grade" value={clsGrade} onChange={(e) => setClsGrade(Number(e.target.value))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400">
                {GRADE_GROUPS.map((grp) => <optgroup key={grp.label} label={grp.label}>{grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}</optgroup>)}
              </select>
              <select data-testid="book-class-section" value={clsSection} onChange={(e) => setClsSection(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400">
                <option value="">Tüm Şubeler</option>
                {[...new Set(students.filter((s) => s.grade === clsGrade).map((s) => s.section).filter(Boolean))].sort().map((s) => <option key={s} value={s}>{s} Şubesi</option>)}
              </select>
              <span data-testid="book-class-chip" className="flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700">
                <Users className="h-4 w-4" /> {clsGrade}. Sınıf{clsSection ? `/${clsSection}` : " (tüm şubeler)"} · {classStudents.length} öğrenci
              </span>
            </div>
          )}
        </div>
      )}

      {books.length > 0 && (
        <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
          <div className="h-2.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      {isAdmin && (targetType === "class" || studentId) && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-sm font-semibold text-slate-700">Kitap Adı</label>
            <input data-testid="book-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="ör. Küçük Prens" className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Sınav Tarihi</label>
            <input data-testid="book-date-input" type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400" />
          </div>
          <button data-testid="book-add-btn" onClick={add} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 font-semibold text-white transition active:scale-95 ${targetType === "class" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-emerald-600 hover:bg-emerald-700"}`}><Plus className="h-5 w-5" /> {targetType === "class" ? "Tüm Sınıfa Ekle" : "Ekle"}</button>
        </div>
      )}

      {targetType === "class" && isPicker ? (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-8 text-center text-sm text-indigo-700">
          <Users className="mx-auto mb-2 h-8 w-8" />
          Yukarıdan kitap adı ve sınav tarihi girip <b>"Tüm Sınıfa Ekle"</b> butonuna basın — kitap seçili sınıftaki <b>{classStudents.length} öğrencinin</b> tümünün listesine eklenecek (zaten ekli olanlar atlanır). Sınav onayları öğrenci bazlı yapılır: onay için "Tek Öğrenci" moduna geçin.
        </div>
      ) : !studentId ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Öğrenci seçin.</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="bg-[#1E293B] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-widest text-white">Okuma Listesi ({books.length} kitap)</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] font-bold uppercase text-slate-500">
                <th className="w-10 px-3 py-2 text-center">#</th>
                <th className="px-3 py-2 text-left">Kitap Adı</th>
                <th className="px-3 py-2 text-left">Sınav Tarihi</th>
                <th className="px-3 py-2 text-center">Durum</th>
                {isAdmin && <th className="px-3 py-2 text-center">İşlem</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {books.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Henüz kitap eklenmedi.</td></tr>
              ) : books.map((b, i) => (
                <tr key={i} data-testid={`book-row-${i}`} className={`transition ${b.approved ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
                  <td className="px-3 py-2.5 text-center font-mono text-xs text-slate-400">{i + 1}</td>
                  <td className="px-3 py-2.5 font-semibold text-slate-800">{b.name}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{b.exam_date ? b.exam_date.split("-").reverse().join(".") : "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {b.approved
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-bold text-white"><CheckCircle2 className="h-3.5 w-3.5" /> Onaylı</span>
                      : <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">Bekliyor</span>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2.5 text-center">
                      <button data-testid={`book-approve-${i}`} onClick={() => approve(i, !b.approved)} className={`rounded-lg p-1.5 transition active:scale-90 ${b.approved ? "bg-emerald-500 text-white" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"}`}><CheckCircle2 className="h-4 w-4" /></button>
                      <button data-testid={`book-remove-${i}`} onClick={() => remove(i)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 active:scale-90"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
